import { Suspense } from "react"
import { makeStyles } from "tss-react/mui"
import { Typography } from "@mui/material"
import { useNavigate } from "react-router-dom"
import { dinero } from "dinero.js"
import { QueryErrorBoundary, QueryErrorFallback, QueryErrorFallbackProps } from "../../../../../core/api/components/QueryErrorBoundary"
import {
    MerchantHomeQuery,
    fetchPosAverageAmount,
    fetchSubscriptionsSales,
    fetchTodaysPosSales,
} from "../merchantHomeApi"
import { DashboardCardSkeleton } from "../../../../dashboards/components/DashboardCardSkeleton"
import { toCurrency, toDinero, toDineroOrUndefined } from "../../../../currency/currencyLib"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { DashboardCard } from "../../../../dashboards/components/DashboardCard"
import { StandardDashboardCard } from "../../components/StandardDashboardCard"
import { MERCHANT_ACCOUNTING_SUBSCRIPTIONS } from "../../../../../constants/routes"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { Period } from "../../../../dashboards/dashboardTypes"
import { useMoney } from "../../../../currency/useMoney"

type TodaysPosOrdersType = Readonly<{ merchantId: number }>

export function TodaysPosOrders({ merchantId }: TodaysPosOrdersType) {
    const moneyFactory = useMoney({})

    const todaysPosSales = useApiQuery(fetchTodaysPosSales, {
        queryName: MerchantHomeQuery.TODAYS_POS_SALES,
        dependencies: {
            merchantId: merchantId,
        },
        suspense: true,
        useErrorBoundary: true,
        refetchInterval: 30000,
    })

    return (
        <>
            <LocalizedStrict id="merchant-home-number-of-orders" attrs={{ title: true, titleTooltip: true }}>
                <StandardDashboardCard
                    variant="number"
                    title="POS orders"
                    titleTooltip="Number of orders tooltip"
                    data={todaysPosSales.data?.posSale.numberOfOrders}
                />
            </LocalizedStrict>
            {todaysPosSales.data && (
                <LocalizedStrict id="merchant-home-turnover" attrs={{ title: true, titleTooltip: true }}>
                    <StandardDashboardCard
                        variant="number"
                        title="POS turnover"
                        titleTooltip="Turnover tooltip"
                        subtitle={todaysPosSales.data.posSale.totalAmount.currency}
                        data={moneyFactory.formatAsNumberWithScale(
                            toDinero(todaysPosSales.data?.posSale.totalAmount),
                            "value",
                        )}
                    />
                </LocalizedStrict>
            )}
        </>
    )
}

// Error Fallback
interface ErrorFallbackProps extends QueryErrorFallbackProps {
    title: string
}

function ErrorFallback({ title, ...props }: ErrorFallbackProps) {
    return (
        <DashboardCard title={title}>
            <QueryErrorFallback {...props} />
        </DashboardCard>
    )
}

// Cards with Error Fallback

type CardsType = Readonly<{ merchantId: number }>

export function PosAverageAmount({ merchantId }: CardsType) {
    const moneyFactory = useMoney({})
    const posAverageAmountResult = useApiQuery(fetchPosAverageAmount, {
        queryName: MerchantHomeQuery.POS_AVERAGE_AMOUNT,
        dependencies: {
            merchantId: merchantId,
            period: Period.TODAY,
        },
        suspense: true,
        useErrorBoundary: true,
        refetchInterval: 30000,
    })

    const currency = toCurrency("DKK")
    const zero = dinero({ amount: 0, scale: 2, currency })

    const totalAmount = posAverageAmountResult.data?.posSales[0]
        ? toDineroOrUndefined(posAverageAmountResult.data.posSales[0].salesSummary.totalAmount) ?? zero
        : zero

    return (
        <LocalizedStrict id="merchant-home-avg-amount" attrs={{ title: true, titleTooltip: true, subtitle: true }}>
            <StandardDashboardCard
                variant="number"
                title="Avg. amount"
                titleTooltip="Avg. amount tooltip"
                subtitle="DKK"
                data={moneyFactory.formatAsNumber(totalAmount)}
            />
        </LocalizedStrict>
    )
}

const useStyles = makeStyles()((theme) => ({
    title: {
        fontWeight: 500,
        color: theme.palette.tailwind.slate[700],
    },
}))

export function SubscriptionsCard({ merchantId }: CardsType) {
    const { classes } = useStyles()
    const navigate = useNavigate()

    const subscriptionsResult = useApiQuery(fetchSubscriptionsSales, {
        queryName: MerchantHomeQuery.TODAYS_SUBSCRIPTIONS_SALES,
        dependencies: {
            merchantId: merchantId,
            period: Period.TODAY,
        },
        suspense: true,
        useErrorBoundary: true,
        refetchInterval: 30000,
    })

    function handleClick() {
        navigate(MERCHANT_ACCOUNTING_SUBSCRIPTIONS)
    }

    return (
        <LocalizedStrict id="merchant-home-subscriptions" attrs={{ title: true, titleTooltip: true }}>
            <DashboardCard
                title="Subscriptions"
                titleTooltip="Subscriptions tooltip"
                onClick={handleClick}
            >
                <Typography variant="h3" className={classes.title}>
                    {subscriptionsResult.data?.subscriptionSale[0].salesSummary.numberOfSubscriptions || 0}
                </Typography>
            </DashboardCard>
        </LocalizedStrict>
    )
}

export function PosSalesCards() {
    const { merchantId } = useMerchant()
    return (
        <>
            <QueryErrorBoundary
                Fallback={(props) => (
                    <>
                        <LocalizedStrict id="merchant-home-number-of-orders" attrs={{ title: true }}>
                            <ErrorFallback {...props} title="Orders" />
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-home-turnover" attrs={{ title: true }}>
                            <ErrorFallback {...props} title="POS turnover" />
                        </LocalizedStrict>
                    </>
                )}
            >
                <Suspense
                    fallback={
                        <>
                            <DashboardCardSkeleton count={2} />
                        </>
                    }
                >
                    <TodaysPosOrders merchantId={merchantId} />
                </Suspense>
            </QueryErrorBoundary>

            <QueryErrorBoundary
                Fallback={(props) => (
                    <LocalizedStrict id="merchant-home-avg-amount" attrs={{ title: true }}>
                        <ErrorFallback {...props} title="Avg. amount" />
                    </LocalizedStrict>
                )}
            >
                <Suspense
                    fallback={
                        <>
                            <DashboardCardSkeleton />
                        </>
                    }
                >
                    <PosAverageAmount merchantId={merchantId} />
                </Suspense>
            </QueryErrorBoundary>

            <QueryErrorBoundary
                Fallback={(props) => (
                    <LocalizedStrict id="merchant-home-subscriptions" attrs={{ title: true }}>
                        <ErrorFallback {...props} title="Subscriptions" />
                    </LocalizedStrict>
                )}
            >
                <Suspense
                    fallback={
                        <>
                            <DashboardCardSkeleton />
                        </>
                    }
                >
                    <SubscriptionsCard merchantId={merchantId} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
